'use client';

/**
 * A container that reveals its `hoverTarget` child while hovered.
 *
 * The template JSON is meant to be self-describing: a renderer should not need a
 * matching stylesheet for the nav to work. Hover cannot be an inline style, so
 * the node declares `hoverGroup` and the renderer supplies the state instead of
 * a `:hover` rule. The builder preview implements this identically, which is
 * what keeps the preview and the published page in step.
 *
 * The panel stays mounted and is hidden with visibility rather than unmounted,
 * so moving the pointer from the trigger onto a link inside it does not close
 * the panel mid-click. The gap between trigger and panel is bridged by padding
 * on the wrapper, for the same reason.
 *
 * Touch devices never fire mouseenter, so a tap on the trigger toggles `open`
 * directly — a tap inside the panel (a real nav link) is left alone so it
 * navigates normally instead of just re-toggling.
 */
import React, { useRef, useState } from 'react';

interface HoverGroupProps {
    style?: React.CSSProperties;
    className?: string;
    trigger: React.ReactNode;
    panel: React.ReactNode;
}

const HoverGroup: React.FC<HoverGroupProps> = ({ style, className, trigger, panel }) => {
    const [open, setOpen] = useState(false);
    const groupRef = useRef<HTMLDivElement>(null);
    const panelWrapRef = useRef<HTMLDivElement>(null);

    return (
        <div
            ref={groupRef}
            style={style}
            className={className}
            onMouseEnter={() => {
                if (!groupRef.current?.closest('.cb-mobile-nav-drawer')) setOpen(true);
            }}
            onMouseLeave={() => {
                if (!groupRef.current?.closest('.cb-mobile-nav-drawer')) setOpen(false);
            }}
            onFocus={() => {
                if (!groupRef.current?.closest('.cb-mobile-nav-drawer')) setOpen(true);
            }}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
            }}
            onClick={(event) => {
                if (groupRef.current?.closest('.cb-mobile-nav-drawer')) return;
                // Only intercept on devices with no real hover (touch) —
                // on a mouse, hover already opened the panel, so a click on
                // the trigger should navigate like any other link.
                const hasHover = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches;
                if (hasHover) return;
                const insidePanel = panelWrapRef.current?.contains(event.target as Node);
                // The panel not being open yet means this tap is "opening"
                // the dropdown, not activating the trigger's own href.
                if (!insidePanel && !open) {
                    event.preventDefault();
                    setOpen(true);
                }
            }}
        >
            <div className="cb-mobile-nav-accordion-heading">
                {trigger}
                <button
                    type="button"
                    className="cb-mobile-nav-accordion-toggle"
                    aria-label={open ? 'Collapse submenu' : 'Expand submenu'}
                    aria-expanded={open}
                    onClick={(event) => {
                        event.stopPropagation();
                        setOpen((value) => !value);
                    }}
                >
                    {open ? '−' : '+'}
                </button>
            </div>
            <div
                ref={panelWrapRef}
                className="cb-nav-hover-panel"
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    // Bridges the visual gap so the pointer never leaves the group.
                    paddingTop: '10px',
                    visibility: open ? 'visible' : 'hidden',
                    opacity: open ? 1 : 0,
                    transition: 'opacity 120ms ease',
                    pointerEvents: open ? 'auto' : 'none',
                    zIndex: 50,
                }}
                aria-hidden={!open}
            >
                {panel}
            </div>
        </div>
    );
};

export default HoverGroup;
